import { request, requestPlain } from '@/utils/request.js'
import { IP } from '@/services/config.js'

/**
 * 从 URL 中获取 token 参数
 * @returns {string|null} token 或 null
 */
export function getTokenFromUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')

  // 优先从 hash 中获取（因为路由在 #/ 后）
  let token = hashParams.get('token')

  // 如果 hash 中没有，再从 search 中获取
  if (!token) {
    token = urlParams.get('token')
  }

  return token
}

/**
 * 初始化 HIS SSO 登录流程
 * 从 URL 获取 token，调用 SSO 登录接口
 * @returns {Promise<Object>} 初始化结果
 */
export async function initHisSsoLogin() {
  try {
    // 1. 从 URL 获取 token
    const token = getTokenFromUrl()

    if (!token) {
      return {
        success: false,
        error: '未获取到有效的 Token',
        hasToken: false,
      }
    }

    console.log('检测到 HIS SSO Token，开始登录...')

    // 2. 调用 SSO 登录接口
    const response = await requestPlain({
      url: '/his/sso/login',
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: `token=${encodeURIComponent(token)}`,
      isEncrypt: false,
      isDecrypt: false,
    })

    console.log('SSO 登录响应:', response)

    if (!response.success) {
      return {
        success: false,
        error: response.message || 'SSO登录失败',
        hasToken: true,
      }
    }
    console.log('SSO 登录成功:', response.result)

    // 3. 设置登录态（按照 Login 组件的方式存储）
    const loginData = response.result
    const authToken = loginData.token
    const user = loginData.userInfo
    const depart = loginData.departs ? loginData.departs[0] : null
    const username = user?.username
    const realname = user?.realname
    const userId = user?.id
    const tenant = loginData.tenantList ? loginData.tenantList[0] : null

    // 存储本地（同时设置 storeToken 以兼容路由守卫）
    localStorage.setItem("token", authToken)
    localStorage.setItem("storeToken", authToken)
    localStorage.setItem("visitNum", response.result.visitNum || "0")
    if (user) localStorage.setItem("user", JSON.stringify(user))
    if (depart) localStorage.setItem("depart", JSON.stringify(depart))
    if (username) localStorage.setItem("username", username)
    if (realname) localStorage.setItem("realname", realname)
    if (userId) localStorage.setItem("userId", userId)
    if (tenant) localStorage.setItem("loginTenantId", JSON.stringify(tenant.id))

    // 4. 清理 URL 中的 token 参数
    cleanTokenFromUrl()

    // 5. 返回成功结果
    return {
      success: true,
      data: loginData,
      hasToken: true,
    }
  } catch (error) {
    console.error('HIS SSO 初始化失败:', error)
    return {
      success: false,
      error: error.message || 'HIS SSO 初始化失败',
      hasToken: true,
    }
  }
}

/**
 * 清理 URL 中的 token 参数
 */
function cleanTokenFromUrl() {
  // 移除 URL 中的 token 参数
  const url = new URL(window.location.href)
  url.searchParams.delete('token')

  // 如果有 hash 参数，也清理
  if (url.hash.includes('?')) {
    const [path, query] = url.hash.split('?')
    const queryParams = new URLSearchParams(query)
    queryParams.delete('token')
    const newQuery = queryParams.toString()
    url.hash = newQuery ? `${path}?${newQuery}` : path
  }

  // 使用 replaceState 清理 URL，不影响浏览器历史记录
  window.history.replaceState({}, '', url.toString())
}

/**
 * 检查当前是否通过 HIS SSO 登录
 * @returns {boolean}
 */
export function isHisSsoLogin() {
  const ssoData = localStorage.getItem('hisSsoData')
  return !!ssoData
}

/**
 * 获取 HIS SSO 登录信息
 * @returns {Object|null}
 */
export function getHisSsoData() {
  const ssoData = localStorage.getItem('hisSsoData')
  if (ssoData) {
    try {
      return JSON.parse(ssoData)
    } catch (error) {
      console.error('解析 HIS SSO 数据失败:', error)
      return null
    }
  }
  return null
}

/**
 * 清除 HIS SSO 登录信息
 */
export function clearHisSsoData() {
  localStorage.removeItem('hisSsoData')
}
